import type { Data, SimulationTable } from "../../../../bext-output-type";
import type {
  AmortizationPlan,
  CalculationParams,
  ComparisonResult,
  CorrectionPlan,
  MonthlySnapshot,
} from "../types";

export function parseBrlToNumber(value: string): number {
  return Number.parseFloat(value.replace(/[^0-9,-]+/g, "").replace(",", "."));
}

interface FetchSimulationParams {
  valorFinanciar: number;
  prazo: number;
  taxaAnual: number;
  amortizationSystemType: number;
  dataInicioFinanciamento: string;
  amortizationPlans: AmortizationPlan[];
  correctionPlans: CorrectionPlan[];
}

export async function fetchFinancingSimulation(
  params: FetchSimulationParams,
): Promise<Data> {
  const {
    valorFinanciar,
    prazo,
    taxaAnual,
    amortizationSystemType,
    dataInicioFinanciamento,
    amortizationPlans,
    correctionPlans,
  } = params;

  const monetaryCorrections: Record<string, number> = {};
  for (const plan of correctionPlans) {
    const start = Math.max(0, plan.startInstallment);
    const end = Math.min(prazo, plan.endInstallment);
    const taxaAoMes = plan.valueAnual / 12;
    for (let i = start; i <= end; i++) {
      monetaryCorrections[i.toString()] = taxaAoMes;
    }
  }

  const amortizations: Record<
    string,
    { value: number; amortizationByTerm: boolean }
  > = {};
  for (const plan of amortizationPlans) {
    if (plan.frequency === "uma_vez") {
      amortizations[plan.startInstallment.toString()] = {
        value: plan.value,
        amortizationByTerm: plan.type === "prazo",
      };
    } else {
      const interval = plan.interval || 1;
      for (let i = plan.startInstallment; i <= prazo; i += interval) {
        amortizations[i.toString()] = {
          value: plan.value,
          amortizationByTerm: plan.type === "prazo",
        };
      }
    }
  }

  const isLocal = window.location.hostname.includes("localhost");
  const url = isLocal ? "https://investidorsardinha.r7.com" : "";

  const response = await fetch(`${url}/wp-json/sardine/v1/amortization`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      loanAmount: valorFinanciar,
      numberOfInstallments: prazo,
      interestRate: taxaAnual,
      amortizationSystemType,
      interestRateType: 0,
      monetaryCorrections,
      amortizations,
      date: dataInicioFinanciamento || new Date().toISOString(),
    }),
  });

  if (!response.ok) {
    throw new Error("Falha ao simular financiamento.");
  }

  const bextData = await response.json();

  if (!bextData?.simulationTable || bextData.simulationTable.length === 0) {
    throw new Error("Resposta de simulação inválida.");
  }

  return bextData as Data;
}

interface ComparisonParams {
  valorImovel: number;
  valorAluguel: number;
  valorizacao: number;
  igpm: number;
  entrada: number;
  custos: number;
  rentabilidade: number;
  hasExtraAmortization: boolean;
}

export function calculateComparison(
  simulationData: Data,
  params: ComparisonParams,
): Omit<ComparisonResult, "simulationData" | "params"> {
  const {
    valorImovel,
    valorAluguel,
    valorizacao,
    igpm,
    entrada,
    custos,
    rentabilidade,
    hasExtraAmortization,
  } = params;

  const simulationTable: SimulationTable[] = simulationData
    .simulationWithAmortizationTable?.length
    ? (simulationData.simulationWithAmortizationTable as SimulationTable[])
    : simulationData.simulationTable;

  const summary = simulationData.summaryWithAmortizationResult
    ?.totalAmountPayable
    ? simulationData.summaryWithAmortizationResult
    : simulationData.summaryResult;

  const rentabilidadeMensal = Math.pow(1 + rentabilidade / 100, 1 / 12) - 1;
  const valorizacaoMensal = Math.pow(1 + valorizacao / 100, 1 / 12) - 1;
  // simulationTable[0] is the disbursement entry (installment=0, installmentAmount=0),
  // so real installments start at index 1.
  const totalMonths = simulationTable.length - 1;

  let investmentBalanceFinancing = 0;
  let investmentBalanceRenting = entrada + custos;
  let totalRent = 0;
  let currentRent = valorAluguel;
  let currentPropertyValue = valorImovel;

  const monthlyData: MonthlySnapshot[] = [];

  // Mes 0: entrada + custos
  monthlyData.push({
    month: 0,
    installment: entrada + custos,
    rent: valorAluguel,
    outstandingBalance: valorImovel - entrada,
    propertyValue: valorImovel,
    investmentFinancing: 0,
    investmentRenting: entrada + custos,
    accumulatedWealthFinancing: valorImovel,
    accumulatedWealthRenting: entrada + custos,
  });

  for (let i = 1; i <= totalMonths; i++) {
    // Reajuste anual do aluguel
    if (i % 12 === 0) {
      currentRent *= 1 + igpm / 100;
    }
    totalRent += currentRent;

    // Valorizacao mensal do imovel
    currentPropertyValue *= 1 + valorizacaoMensal;

    const entry = simulationTable[i];
    const prestacao = entry?.installmentAmount || 0;
    const saldoDevedor = entry?.outstandingBalance || 0;

    // Investimento da diferenca
    const investRenting = Math.max(0, prestacao - currentRent);
    const investFinancing = Math.max(0, currentRent - prestacao);

    // Atualizar patrimonios com rentabilidade
    investmentBalanceRenting =
      investmentBalanceRenting * (1 + rentabilidadeMensal) + investRenting;
    investmentBalanceFinancing =
      investmentBalanceFinancing * (1 + rentabilidadeMensal) + investFinancing;

    monthlyData.push({
      month: i,
      installment: prestacao,
      rent: currentRent,
      outstandingBalance: saldoDevedor,
      propertyValue: currentPropertyValue,
      investmentFinancing: investFinancing,
      investmentRenting: investRenting,
      accumulatedWealthFinancing:
        currentPropertyValue + investmentBalanceFinancing,
      accumulatedWealthRenting: investmentBalanceRenting,
    });
  }

  const totalPaidFinancing = summary.totalAmountPayable + custos + entrada;
  const totalInvestedFinancing = monthlyData.reduce(
    (sum, m) => sum + m.investmentFinancing,
    0,
  );
  const totalInvestedRenting = monthlyData.reduce(
    (sum, m) => sum + m.investmentRenting,
    0,
  );

  const finalWealthFinancing =
    currentPropertyValue + investmentBalanceFinancing;
  const finalWealthRenting = investmentBalanceRenting;

  return {
    summary: {
      totalPaidFinancing,
      totalPaidRent: totalRent,
      totalInvestedFinancing,
      totalInvestedRenting,
      finalWealthFinancing,
      finalWealthRenting,
      finalPropertyValue: currentPropertyValue,
      bestChoice:
        finalWealthFinancing > finalWealthRenting ? "financing" : "renting",
    },
    monthlyData,
    hasExtraAmortization,
  };
}

export async function calculateRentVsBuy(
  params: CalculationParams,
): Promise<ComparisonResult> {
  const {
    valorImovel,
    entrada,
    prazo,
    taxaAnual,
    amortizationSystemType,
    dataInicioFinanciamento,
    amortizationPlans = [],
    correctionPlans = [],
    valorAluguel,
    valorizacao,
    igpm,
    custos,
    rentabilidade,
  } = params;

  const simulationData = await fetchFinancingSimulation({
    valorFinanciar: valorImovel - entrada,
    prazo,
    taxaAnual,
    amortizationSystemType,
    dataInicioFinanciamento,
    amortizationPlans,
    correctionPlans,
  });

  const comparison = calculateComparison(simulationData, {
    valorImovel,
    valorAluguel,
    valorizacao,
    igpm,
    entrada,
    custos,
    rentabilidade,
    hasExtraAmortization: (params.amortizationPlans?.length || 0) > 0,
  });

  return {
    ...comparison,
    simulationData,
    params: {
      valorImovel,
      rentabilidade,
      valorizacao,
      igpm,
      amortizationSystemType,
      entrada,
      prazo,
    },
    hasExtraAmortization: (params.amortizationPlans?.length || 0) > 0,
  };
}
